import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import React, {useState} from 'react'
import {GridBlock} from 'Event/Marketplace/Block/Grid'
import {useConfigurableSection} from 'organization/Marketplace/config/ConfigurableSection'
import VisibilitySetting from 'organization/Marketplace/config/BlockConfig/VisibilitySettings'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import CollapsibleSection from 'lib/ui/ConfigPanel/CollapsibleSection'
import {Controller} from 'react-hook-form'
import Slider from 'lib/ui/Slider'
import {useConfig} from 'organization/Event/Configurable'
import ColorPicker from 'lib/ui/ColorPicker'
import ImageAssetUploader from 'lib/asset/ImageAssetUploader'
import {Label} from 'lib/ui/typography'
import MarginPaddingInputs from 'lib/ui/MarginPaddingInputs'
import {REMOVE} from 'lib/JsonUpdateProvider'
import Select from 'lib/ui/Select'
import {onUnknownChangeHandler} from 'lib/dom'
import Option from 'lib/ui/Select/Option'
import MoveSectionButtons from 'organization/Marketplace/config/BlockConfig/MoveSectionButtons'
import {useSection} from 'Event/Marketplace/Section'

interface GridConfigProps extends GridBlock {
  id: string
  onMoveSectionUp?: () => void
  onMoveSectionDown?: () => void
}

export default function GridConfig(props: GridConfigProps) {
  const {
    id,
    background,
    numColumns,
    padding,
    blockSpacing,
    alignment,
    onMoveSectionUp,
    onMoveSectionDown,
  } = props
  const [visibility, setVisibility] = useState(props.visibility)

  const {
    form: {control},
  } = useConfig()

  const {update} = useConfigurableSection()
  const {calculateVisibility} = useSection()

  const handleRemove = () => {
    update({
      blocks: {
        [id]: REMOVE,
      },
    })
    calculateVisibility()
  }

  const save = (data: any) => {
    data.visibility = visibility
    update({
      blocks: {
        [id]: data,
      },
    })
    calculateVisibility()
  }

  return (
    <Config title="Grid Block" onSave={save} onRemove={handleRemove}>
      <SettingsPanel>
        <VisibilitySetting
          visibility={visibility}
          setVisibility={setVisibility}
        />

        <MoveSectionButtons
          onMoveUp={onMoveSectionUp}
          onMoveDown={onMoveSectionDown}
        />
      </SettingsPanel>
      <StylingPanel>
        <CollapsibleSection
          label="Background"
          storageKey={`blocks-${id}-background`}
        >
          <Controller
            name={`background.image`}
            control={control}
            defaultValue={background.image}
            render={({onChange, value}) => (
              <ImageAssetUploader
                onChange={onChange}
                value={value}
                uploadLabel="Upload"
                uploadInputProps={{
                  'aria-label': 'block background image',
                }}
                width={1920}
                height={1200}
                canResize
                disableAutoRemove
              />
            )}
          />
          <Controller
            name={`background.color`}
            defaultValue={background.color}
            control={control}
            render={({value, onChange}) => (
              <ColorPicker
                label="Color"
                color={value}
                onPick={onChange}
                aria-label="block background color"
              />
            )}
          />
          <Controller
            name={`background.opacity`}
            defaultValue={background.opacity}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Opacity"
                unit="%"
                valueLabelDisplay="auto"
                aria-label="block background opacity"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
              />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Advanced Spacing"
          storageKey={`sections-blocks-${id}-spacing`}
        >
          <Controller
            name={`numColumns`}
            defaultValue={numColumns}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Number of Columns Wide"
                min={1}
                max={4}
                hideUnits
                onChange={onChange}
                valueLabelDisplay="off"
                value={value}
              />
            )}
          />
          <Label>Padding</Label>
          <MarginPaddingInputs
            control={control}
            namePrefix={`padding`}
            values={padding}
          />
          <Controller
            name={`blockSpacing`}
            defaultValue={blockSpacing ?? 10}
            control={control}
            render={({value, onChange}) => (
              <Slider
                label="Block Spacing"
                value={value}
                onChange={onChange}
                step={1}
                min={0}
                max={100}
                aria-label="block spacing"
              />
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection
          label="Alignment"
          storageKey={`blocks-${id}-alignment`}
        >
          <Controller
            name={`alignment.vertical`}
            defaultValue={alignment.vertical}
            control={control}
            render={({value, onChange}) => (
              <Select
                fullWidth
                label="Vertical"
                value={value}
                onChange={onUnknownChangeHandler(onChange)}
              >
                <Option value="start">Top</Option>
                <Option value="center">Center</Option>
                <Option value="end">Bottom</Option>
              </Select>
            )}
          />
        </CollapsibleSection>
        <CollapsibleSection label="Border" storageKey={`blocks-${id}-border`}>
          <Controller
            name={`border.width`}
            defaultValue={props.border.width}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={50}
                step={1}
                onChange={onChange}
                value={value}
                label="Thickness"
              />
            )}
          />
          <Controller
            name={`border.radius`}
            defaultValue={props.border.radius}
            control={control}
            render={({value, onChange}) => (
              <Slider
                min={0}
                max={50}
                step={1}
                onChange={onChange}
                value={value}
                label="Radius"
              />
            )}
          />
          <Controller
            name={`border.color`}
            defaultValue={props.border.color}
            control={control}
            render={({value, onChange}) => (
              <ColorPicker label="Color" color={value} onPick={onChange} />
            )}
          />
        </CollapsibleSection>
      </StylingPanel>
    </Config>
  )
}
